import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import AchieverListApi from '../../../middleware/api/Achievements/AchieverList/AchieverListApi'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import {
  AchievementHistoryTimeline,
  AchievementHistoryTimelineQueryParameters,
  AchieverListQueryParameters,
  AchieverListSliceState,
  AchieverListUserTypes,
  IncomingAchievementHistoryTimelineList,
  IncomingAchieverListType,
  UpdateShowOnDashboardQueryParameters,
} from '../../../types/Achievements/AchieverList/AchieverListTypes'
import { ValidationError } from '../../../types/SidebarMenu/sidebarMenuType'
import { AppDispatch, RootState } from '../../../stateStore'
import { selectAchievementType } from '../../../pages/Achievements/AchievementConstants'

const sortAchieverListByDatesDescending = (list: AchieverListUserTypes[]) => {
  const sortedList = (a: AchieverListUserTypes, b: AchieverListUserTypes) => {
    return (
      new Date(b.createdDate.split('/').reverse().join('-')).getTime() -
      new Date(a.createdDate.split('/').reverse().join('-')).getTime()
    )
  }
  return list?.sort(sortedList)
}

const sortAchievementTimelineByDatesDescending = (
  list: AchievementHistoryTimeline[],
) => {
  return list?.sort((a, b) => {
    const aDate = new Date(a.modifiedDate).getTime()
    const bDate = new Date(b.modifiedDate).getTime()
    return bDate - aDate
  })
}

const getAllAchieverList = createAsyncThunk(
  'achieverList/getAllAchieverList',
  async (query: AchieverListQueryParameters, thunkApi) => {
    try {
      const data = await AchieverListApi.getAllAchievements(query)
      return thunkApi.fulfillWithValue(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const updateAchievementDashboardStatus = createAsyncThunk(
  'achieverList/updateAchievementDashboardStatus',
  async (query: UpdateShowOnDashboardQueryParameters, thunkApi) => {
    try {
      await AchieverListApi.showOnDashBoard(query)
      return thunkApi.fulfillWithValue(query.achievementId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAchievementHistoryTimeline = createAsyncThunk(
  'achieverList/getAchievementHistoryTimeline',
  async (query: AchievementHistoryTimelineQueryParameters, thunkApi) => {
    try {
      const data = await AchieverListApi.getAchievementHistoryTimeline(query)
      return thunkApi.fulfillWithValue(data)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteAchievement = createAsyncThunk<
  number | undefined,
  number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'leaveSettings/deleteEmployeeLeaveCategory',
  async (achievementId, thunkApi) => {
    try {
      return await AchieverListApi.deleteAchievement(achievementId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialState: AchieverListSliceState = {
  currentPage: 1,
  pageSize: 20,
  isLoading: ApiLoadingState.idle,
  achieverList: { size: 0, list: [] },
  achievementHistoryTimeline: { size: 0, list: [] },
  successOnUpdateShowOnDashboard: null,

  selectedAchievementId: '',
  selectedAchievementName: selectAchievementType,
  fromDate: '',
  toDate: '',
  fromWhere: '',
}

const achieverListSlice = createSlice({
  name: 'achieverList',
  initialState,
  reducers: {
    clearAll: (state) => {
      state.fromDate = ''
      state.toDate = ''
      state.selectedAchievementId = ''
      state.selectedAchievementName = selectAchievementType
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setIsLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setClearUpdateStatus: (state) => {
      state.successOnUpdateShowOnDashboard = null
    },
    setSelectedAchievementId: (state, action) => {
      state.selectedAchievementId = action.payload
    },
    setSelectedAchievementName: (state, action) => {
      state.selectedAchievementName = action.payload
    },
    setFromDate: (state, action) => {
      state.fromDate = action.payload
    },
    setToDate: (state, action) => {
      state.toDate = action.payload
    },
    setFromWhere: (state, action) => {
      state.fromWhere = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllAchieverList.fulfilled, (state, action) => {
      const data = JSON.parse(JSON.stringify(action.payload))
      const sortedData = sortAchieverListByDatesDescending(data.list)
      const toSendData = {
        size: data.size,
        list: sortedData,
      } as IncomingAchieverListType
      state.achieverList = toSendData
    })
    builder.addCase(
      getAchievementHistoryTimeline.fulfilled,
      (state, action) => {
        const data = JSON.parse(JSON.stringify(action.payload))
        const sortedData = sortAchievementTimelineByDatesDescending(data.list)
        const toSendData = {
          size: data.size,
          list: sortedData,
        } as IncomingAchievementHistoryTimelineList
        state.achievementHistoryTimeline = toSendData
      },
    )
    builder.addCase(
      updateAchievementDashboardStatus.fulfilled,
      (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.successOnUpdateShowOnDashboard = Number(action.payload)
      },
    )
    builder.addCase(
      updateAchievementDashboardStatus.rejected,
      (state, action) => {
        state.isLoading = ApiLoadingState.failed
        state.successOnUpdateShowOnDashboard = Number(action.payload) * -1
      },
    )
    builder.addMatcher(
      isAnyOf(
        getAllAchieverList.fulfilled,
        getAchievementHistoryTimeline.fulfilled,
      ),
      (state) => {
        state.isLoading = ApiLoadingState.succeeded
      },
    )
    builder.addMatcher(
      isAnyOf(
        getAllAchieverList.pending,
        updateAchievementDashboardStatus.pending,
        getAchievementHistoryTimeline.pending,
      ),
      (state) => {
        state.isLoading = ApiLoadingState.loading
      },
    )
    builder.addMatcher(
      isAnyOf(
        getAllAchieverList.rejected,
        getAchievementHistoryTimeline.rejected,
      ),
      (state) => {
        state.isLoading = ApiLoadingState.failed
      },
    )
  },
})

const AchievementListSize = (state: RootState): number =>
  state.achieverList.achieverList?.size

const AchievementListData = (state: RootState): AchieverListUserTypes[] =>
  state.achieverList.achieverList?.list

const GetSelectedAchievementId = (state: RootState): number | null | string =>
  state.achieverList.selectedAchievementId

const GetSelectedAchievementName = (state: RootState): string =>
  state.achieverList.selectedAchievementName

const GetFromDate = (state: RootState): string => state.achieverList.fromDate

const GetToDate = (state: RootState): string => state.achieverList.toDate

const GetFromWhere = (state: RootState): string => state.achieverList.fromWhere

export const achieverListThunks = {
  getAllAchieverList,
  updateAchievementDashboardStatus,
  getAchievementHistoryTimeline,
  deleteAchievement,
}

export const achieverListSelectors = {
  AchievementListSize,
  AchievementListData,
  GetSelectedAchievementId,
  GetSelectedAchievementName,
  GetFromDate,
  GetFromWhere,
  GetToDate,
}

export const achieverListService = {
  ...achieverListThunks,
  actions: achieverListSlice.actions,
  selectors: achieverListSelectors,
}

const achieverListReducer = achieverListSlice.reducer
export default achieverListReducer
